import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Check, Gift, Loader2, Shield, Star, CreditCard, User, Calendar, Phone, MapPin, Mail, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PlaceAutocompleteInput from './PlaceAutocompleteInput';
import { MaskedDateInput, MaskedTimeInput } from './premium/MaskedDateTime';
import useBlessingClick from '../hooks/useBlessingClick';

import { API_BASE } from "../utils/apiBase";

const API_URL = API_BASE;
// Razorpay Key - Read from environment (required)
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const IntakeFormModal = ({ isOpen, onClose, onSuccess }) => {
  const { t, language } = useLanguage();
  const blessing = useBlessingClick();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    gender: '',
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: '',
    place_latitude: null,
    place_longitude: null,
    email: '',
    whatsapp_number: '',
    language: language === 'hi' ? 'hindi' : 'english',
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      language: language === 'hi' ? 'hindi' : 'english'
    }));
  }, [language]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      toast.error(language === 'hi' ? 'कृपया अपना नाम भरें' : 'Please enter your name');
      return false;
    }
    if (!formData.gender) {
      toast.error(language === 'hi' ? 'कृपया लिंग चुनें' : 'Please select gender');
      return false;
    }
    if (!formData.date_of_birth) {
      toast.error(language === 'hi' ? 'कृपया जन्म तिथि भरें' : 'Please enter date of birth');
      return false;
    }
    if (!formData.time_of_birth) {
      toast.error(language === 'hi' ? 'कृपया जन्म का समय भरें' : 'Please enter time of birth');
      return false;
    }
    if (!formData.place_of_birth.trim()) {
      toast.error(language === 'hi' ? 'कृपया जन्म स्थान भरें' : 'Please enter place of birth');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast.error(language === 'hi' ? 'कृपया सही ईमेल भरें' : 'Please enter valid email');
      return false;
    }
    const phoneDigits = (formData.whatsapp_number || '').replace(/\D/g, '');
    const last10 = phoneDigits.slice(-10);
    if (last10.length !== 10 || !/^[6-9]/.test(last10)) {
      toast.error(language === 'hi' ? 'कृपया सही 10 अंकों का मोबाइल नंबर भरें (6/7/8/9 से शुरू)' : 'Please enter a valid 10-digit mobile starting with 6/7/8/9');
      return false;
    }
    if (new Set(last10).size <= 2) {
      toast.error(language === 'hi' ? 'कृपया अपना असली WhatsApp नंबर भरें — same digit repeat नहीं चलेगा' : 'Please enter your real WhatsApp number — repeating digits are not accepted');
      return false;
    }
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Read Meta browser cookies — pass them in the body too, so server-side
      // CAPI gets attribution even when cookies don't flow cross-origin.
      const getCookie = (name) => {
        const m = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
        return m ? decodeURIComponent(m[3]) : null;
      };
      const fbp = getCookie('_fbp');
      const fbc = getCookie('_fbc');

      // SINGLE FLOW for every device — Razorpay-hosted Payment Link redirect.
      // No popup, no SDK, no iframe. Works reliably on desktop browsers,
      // every mobile browser, and even in-app webviews.
      const linkResp = await axios.post(`${API_URL}/api/orders/payment-link`, {
        customer_info: {
          full_name: formData.full_name,
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          time_of_birth: formData.time_of_birth,
          place_of_birth: formData.place_of_birth,
          place_latitude: formData.place_latitude,
          place_longitude: formData.place_longitude,
          email: formData.email,
          whatsapp_number: formData.whatsapp_number,
          language: formData.language,
        },
        amount: 9900,
        currency: 'INR',
        meta_fbp: fbp,
        meta_fbc: fbc,
        meta_event_source_url: window.location.href,
      }, { withCredentials: true });

      const shortUrl = linkResp.data?.short_url;
      if (!shortUrl) {
        toast.error(language === 'hi' ? 'भुगतान लिंक नहीं बन सका' : 'Payment link could not be created');
        setIsLoading(false);
        return;
      }

      if (window.fbq) {
        // Last intent signal before redirecting off-site to Razorpay. This
        // event will land even if the user bounces back — the Pixel cookie
        // has already been set on karmakundali.org.
        window.fbq('track', 'AddPaymentInfo', {
          value: 99,
          currency: 'INR',
          content_name: 'Karma Kundali Premium Report',
          content_ids: [linkResp.data?.order_id || 'kundali-premium'],
        });
        window.fbq('track', 'InitiateCheckout', {
          value: 99,
          currency: 'INR',
          content_ids: [linkResp.data?.order_id || 'kundali-premium'],
          num_items: 1,
        });
      }

      // Full-page navigate to Razorpay's hosted page.
      window.location.href = shortUrl;
    } catch (error) {
      console.error('Payment error:', error?.response?.data || error);
      // Surface backend's specific 400 reason (e.g. invalid phone) to the user
      // instead of the generic "Something went wrong".
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(language === 'hi' ? 'कुछ गलत हो गया' : 'Something went wrong');
      }
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-brand-cream border-2 border-brand-gold/40 max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-premium p-0"
        data-testid="intake-form-modal"
      >
        {/* Premium maroon header */}
        <div className="relative bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] px-6 md:px-8 pt-8 pb-7 text-center overflow-hidden">
          <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-25 pointer-events-none" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 50% 30%, rgba(243,229,171,0.20) 0%, transparent 60%)' }}
          />
          <DialogHeader className="relative z-10 space-y-2 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="w-8 h-px bg-brand-gold/60" />
              <span className="text-brand-gold-light text-[10px] uppercase tracking-[0.35em] font-semibold">
                {language === 'hi' ? 'अभी ऑर्डर करें' : 'Order Now'}
              </span>
              <span className="w-8 h-px bg-brand-gold/60" />
            </div>
            <DialogTitle
              className={`font-playfair font-bold text-2xl md:text-3xl text-brand-gold-light text-center leading-tight ${language === 'hi' ? 'font-hindi-serif' : ''}`}
            >
              {t('form_title')}
            </DialogTitle>
            <p className={`text-white/75 text-sm md:text-base ${language === 'hi' ? 'font-hindi' : 'font-outfit'}`}>
              {language === 'hi'
                ? 'सटीक कुंडली के लिए सही जानकारी भरें'
                : 'Fill accurate details for a precise Kundali'}
            </p>
          </DialogHeader>
        </div>

        {/* Single Page Form */}
        <div className="space-y-6 p-6 md:p-8 bg-brand-cream">
          {/* Personal Info Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-brand-maroon font-bold">
              <User className="w-5 h-5" />
              <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                {language === 'hi' ? 'व्यक्तिगत जानकारी' : 'Personal Information'}
              </span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_full_name')} *
                </Label>
                <Input
                  id="full_name"
                  data-testid="input-full-name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder={language === 'hi' ? 'अपना पूरा नाम' : 'Your full name'}
                  className="bg-white border-brand-gold/30 focus:border-brand-gold focus:ring-brand-gold/20 h-12 rounded-xl"
                />
              </div>

              {/* Gender - Only Male/Female */}
              <div className="space-y-2">
                <Label className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_gender')} *
                </Label>
                <RadioGroup
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange('gender', value)}
                  className="flex gap-3 h-12 items-center"
                >
                  <div className="flex items-center">
                    <RadioGroupItem value="male" id="male" data-testid="radio-male" className="sr-only peer" />
                    <Label 
                      htmlFor="male" 
                      className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 cursor-pointer transition-all
                                  ${formData.gender === 'male' 
                                    ? 'border-brand-maroon bg-brand-maroon text-brand-gold-light font-bold shadow-maroon-soft' 
                                    : 'border-brand-gold/30 bg-white text-brand-text-soft hover:border-brand-gold'}`}
                    >
                      <User className="w-4 h-4" />
                      <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>{t('form_gender_male')}</span>
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <RadioGroupItem value="female" id="female" data-testid="radio-female" className="sr-only peer" />
                    <Label 
                      htmlFor="female" 
                      className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 cursor-pointer transition-all
                                  ${formData.gender === 'female' 
                                    ? 'border-brand-maroon bg-brand-maroon text-brand-gold-light font-bold shadow-maroon-soft' 
                                    : 'border-brand-gold/30 bg-white text-brand-text-soft hover:border-brand-gold'}`}
                    >
                      <User className="w-4 h-4" />
                      <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>{t('form_gender_female')}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Report language picker — explicit choice, independent of the
                site's language toggle. Some users browse in Hindi but want
                their PDF in English (or vice versa) — let them choose. */}
            <div className="space-y-2">
              <Label className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? 'रिपोर्ट किस भाषा में चाहिए?' : 'Report Language'} *
              </Label>
              <RadioGroup
                value={formData.language}
                onValueChange={(value) => handleInputChange('language', value)}
                className="flex gap-3 h-12 items-center"
              >
                <div className="flex items-center">
                  <RadioGroupItem value="hindi" id="lang-hindi" data-testid="radio-lang-hindi" className="sr-only peer" />
                  <Label
                    htmlFor="lang-hindi"
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 cursor-pointer transition-all font-hindi
                                ${formData.language === 'hindi'
                                  ? 'border-brand-maroon bg-brand-maroon text-brand-gold-light font-bold shadow-maroon-soft'
                                  : 'border-brand-gold/30 bg-white text-brand-text-soft hover:border-brand-gold'}`}
                  >
                    हिन्दी में
                  </Label>
                </div>
                <div className="flex items-center">
                  <RadioGroupItem value="english" id="lang-english" data-testid="radio-lang-english" className="sr-only peer" />
                  <Label
                    htmlFor="lang-english"
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 cursor-pointer transition-all
                                ${formData.language === 'english'
                                  ? 'border-brand-maroon bg-brand-maroon text-brand-gold-light font-bold shadow-maroon-soft'
                                  : 'border-brand-gold/30 bg-white text-brand-text-soft hover:border-brand-gold'}`}
                  >
                    In English
                  </Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-brand-text-mute">
                {language === 'hi'
                  ? 'आपकी कुंडली PDF इसी भाषा में बनेगी।'
                  : 'Your Kundali PDF will be generated in this language.'}
              </p>
            </div>
          </div>

          {/* Birth Details Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-brand-maroon font-bold">
              <Calendar className="w-5 h-5" />
              <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                {language === 'hi' ? 'जन्म विवरण' : 'Birth Details'}
              </span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Date of Birth — DD/MM/YYYY masked input (premium mobile) */}
              <div className="space-y-2">
                <Label htmlFor="dob" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_dob')} *
                </Label>
                <MaskedDateInput
                  id="dob"
                  testId="input-dob"
                  value={formData.date_of_birth}
                  onChange={(v) => handleInputChange('date_of_birth', v)}
                  required
                  placeholder={language === 'hi' ? 'दिन/माह/वर्ष' : 'DD/MM/YYYY'}
                />
              </div>

              {/* Time of Birth — HH:MM 24-hour masked input */}
              <div className="space-y-2">
                <Label htmlFor="tob" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_tob')} *
                </Label>
                <MaskedTimeInput
                  id="tob"
                  testId="input-tob"
                  value={formData.time_of_birth}
                  onChange={(v) => handleInputChange('time_of_birth', v)}
                  required
                  placeholder={language === 'hi' ? 'घंटा:मिनट (24h)' : 'HH:MM (24h)'}
                />
              </div>

              {/* Place of Birth */}
              <div className="space-y-2">
                <Label htmlFor="pob" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_pob')} *
                </Label>
                <PlaceAutocompleteInput
                  value={formData.place_of_birth}
                  onChange={(v) => setFormData(prev => ({
                    ...prev,
                    place_of_birth: v,
                    place_latitude: null,
                    place_longitude: null,
                  }))}
                  onPlaceSelected={({ place, latitude, longitude }) => {
                    setFormData(prev => ({
                      ...prev,
                      place_of_birth: place,
                      place_latitude: latitude,
                      place_longitude: longitude,
                    }));
                  }}
                  language={language}
                  testId="input-pob"
                />
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-brand-maroon font-bold">
              <Phone className="w-5 h-5" />
              <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                {language === 'hi' ? 'संपर्क जानकारी' : 'Contact Information'}
              </span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_email')} *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold-dark" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="email@example.com"
                    className="bg-white border-brand-gold/30 focus:border-brand-gold focus:ring-brand-gold/20 h-12 pl-10 rounded-xl"
                  />
                </div>
              </div>

              {/* WhatsApp */}
              <div className="space-y-2">
                <Label htmlFor="whatsapp" className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_whatsapp')} *
                </Label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold-dark" />
                  <Input
                    id="whatsapp"
                    type="tel"
                    data-testid="input-whatsapp"
                    value={formData.whatsapp_number}
                    onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                    placeholder="+91 9876543210"
                    className="bg-white border-brand-gold/30 focus:border-brand-gold focus:ring-brand-gold/20 h-12 pl-10 rounded-xl"
                  />
                </div>
              </div>

              {/* Language */}
              <div className="space-y-2">
                <Label className={`text-brand-text-dark font-medium ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {t('form_language')} *
                </Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => handleInputChange('language', value)}
                >
                  <SelectTrigger
                    data-testid="select-language"
                    className="bg-white border-brand-gold/30 focus:border-brand-gold focus:ring-brand-gold/20 h-12 rounded-xl"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-brand-gold/30">
                    <SelectItem value="hindi">हिंदी (Hindi)</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-gradient-to-br from-brand-cream via-white to-brand-gold-light/30 rounded-2xl p-5 md:p-6 border-2 border-brand-gold/40">
            <div className="flex items-center gap-2 text-brand-maroon font-bold mb-4">
              <CreditCard className="w-5 h-5" />
              <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                {language === 'hi' ? 'ऑर्डर सारांश' : 'Order Summary'}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-gold-dark" />
                  <span className={`text-brand-text-dark ${language === 'hi' ? 'font-hindi' : 'font-outfit'}`}>{t('pricing_base')}</span>
                </div>
                <span className="font-bold text-brand-text-dark">₹99</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-brand-gold-dark" />
                  <div>
                    <span className={`text-brand-text-dark ${language === 'hi' ? 'font-hindi' : 'font-outfit'}`}>{t('pricing_addon')}</span>
                    <span className="ml-2 text-[10px] bg-brand-maroon text-brand-gold-light px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">{t('pricing_addon_tag')}</span>
                  </div>
                </div>
                <span className="font-bold text-green-700">{language === 'hi' ? 'मुफ़्त' : 'FREE'}</span>
              </div>

              <div className="border-t border-brand-gold/30 pt-3 flex items-center justify-between">
                <span className={`font-bold text-lg text-brand-maroon ${language === 'hi' ? 'font-hindi-serif' : 'font-playfair'}`}>{t('pricing_total')}</span>
                <span className="font-playfair font-black text-3xl text-brand-maroon">₹99</span>
              </div>
            </div>
          </div>

          {/* Pay Button */}
          <button
            onClick={(e) => { blessing(e); handlePayment(); }}
            disabled={isLoading}
            data-testid="form-submit-btn"
            className="w-full bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-bold text-lg py-5 rounded-full shadow-maroon-soft hover:shadow-premium hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                  {language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...'}
                </span>
              </>
            ) : (
              <>
                <Star className="w-5 h-5" />
                <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
                  {t('form_submit')} — ₹99
                </span>
              </>
            )}
          </button>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-brand-text-mute text-sm">
            <Shield className="w-4 h-4 text-green-700" />
            <span className={language === 'hi' ? 'font-hindi' : 'font-outfit'}>
              {language === 'hi' ? '100% सुरक्षित भुगतान • Razorpay द्वारा संरक्षित' : '100% Secure Payment • Protected by Razorpay'}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntakeFormModal;
